import _ChevronRight from "@mui/icons-material/ChevronRight";
import _Skeleton from "@mui/material/Skeleton";
import _Typography from "@mui/material/Typography";
import _Box from "@mui/material/Box";
import _Button2 from "@mui/material/Button";
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUnits } from '../../../hooks';
import useEventRankingQuery from '../../../hooks/use-event-ranking-query';
function BfgScoreDetailCard({
  Button: _Button,
  challenge,
  event,
  onFocusOwnCompanyInLeaderboard,
  ownGeogroup,
  selectLeaderboardScope,
  selectTab
}) {
  const {
    t
  } = useTranslation();
  const {
    formatNumber,
    toOrdinal
  } = useUnits();
  const {
    data: ownRanking
  } = useEventRankingQuery(ownGeogroup ? event.id : undefined, {
    page: 1,
    pageSize: 1,
    groupTypes: ['company'],
    groupsIds: ownGeogroup ? [ownGeogroup.id] : undefined
  });
  const ownItem = ownGeogroup === null ? null : ownRanking ? ownRanking.items[0] ?? null : undefined;
  if (ownItem === null) return /*#__PURE__*/React.createElement(React.Fragment, null);
  const Button = _Button || _Button2;

  // The ranking endpoint only exposes the final weighted score (progressValue), not its two
  // components — but the formula is fixed and documented (see bfg-score-formula.tsx's FAQ
  // example): progressValue = rawPoints × (activeParticipantsCount / declaredEmployeeCount). The
  // active count IS on the ranking item, and declaredEmployeeCount comes from the challenge, so
  // the active rate and raw points can be backed out from them.
  const declaredEmployeeCount = challenge?.commonProgressWeightingDetails?.declaredEmployeeCount;
  const activeRatio = ownItem && declaredEmployeeCount && declaredEmployeeCount > 0 ? ownItem.activeParticipantsCount / declaredEmployeeCount : undefined;
  const rawPoints = ownItem && activeRatio ? Math.round(ownItem.progressValue / activeRatio) : undefined;

  // Same day-count formula as Challenge.daysLeft — computed from the event's own dates rather
  // than the company's internal challenge, since the two can diverge (see event.period usage
  // above): trusting challenge.period here previously showed "Terminé" while the event was
  // still ongoing.
  const eventDaysLeft = (() => {
    const diff = event.endDate.diff(moment(), 'days', true);
    return moment().isAfter(event.endDate) ? Math.ceil(diff - 1) : Math.ceil(diff);
  })();
  return /*#__PURE__*/React.createElement(_Box, {
    border: "1px solid #C9E9F8",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: 4,
    sx: {
      background: 'linear-gradient(90deg, #F1F9F5 0%, #EAF8FE 100%)'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#CDF5E3",
    borderRadius: 8,
    display: "flex",
    gap: 0.5,
    paddingX: 1,
    paddingY: 0.5,
    width: "fit-content"
  }, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#048760",
    borderRadius: "50%",
    flexShrink: 0,
    height: 8,
    width: 8
  }), /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      color: '#048760',
      fontSize: 12,
      fontWeight: 700
    }
  }, event.period === 'past' ? t('commons.done') : `${t('commons.challenges.bfg_score_detail.ongoing')} · ${t('commons.challenges.days_left', {
    count: eventDaysLeft
  })}`)), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 3,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexWrap: "wrap",
    gap: 5
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 12,
      fontWeight: 600
    }
  }, t('commons.challenges.bfg_score_detail.rank_label')), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "baseline",
    display: "flex",
    gap: 1
  }, ownItem ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 32,
      fontWeight: 800
    }
  }, toOrdinal(ownItem.rank)), /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      color: '#2c2e30',
      fontSize: 16,
      fontWeight: 700
    }
  }, t('commons.challenges.bfg_score_detail.participants', {
    count: event.numberOfParticipants
  }))) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 32,
    variant: "text",
    width: 140
  }))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 12,
      fontWeight: 600
    }
  }, t('commons.challenges.bfg_score_detail.score_label')), ownItem ? /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    width: 136
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 32,
      fontWeight: 800
    }
  }, formatNumber(ownItem.progressValue.toFixed(1))), activeRatio !== undefined && rawPoints !== undefined && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#D9E7FF",
    borderRadius: 8,
    height: 8,
    overflow: "hidden",
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#2AC682",
    borderRadius: 8,
    height: "100%",
    width: `${Math.round(activeRatio * 100)}%`
  })), /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      color: '#666a6f',
      fontSize: 12
    }
  }, t('commons.challenges.bfg_score_detail.score_caption', {
    points: formatNumber(rawPoints),
    percent: Math.round(activeRatio * 100)
  })))) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 32,
    variant: "text",
    width: 100
  }))), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    endIcon: /*#__PURE__*/React.createElement(_ChevronRight, {
      fontSize: "small"
    }),
    onClick: () => {
      selectTab('leaderboard');
      selectLeaderboardScope('national');
      if (ownItem) onFocusOwnCompanyInLeaderboard?.(ownItem.leaderboardIndex);
    },
    size: "medium",
    variant: "text"
  }, t('commons.challenges.bfg_score_detail.see_position'))));
}
export default BfgScoreDetailCard;